import {gql} from '@apollo/client';
import {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

interface RegistryNodeForValidSchemasSummaryQuery {
  __typename: 'RegistryNode';
  id: string;
  validAntibodySchemas: Array<{
    __typename: 'AntibodyEnforcedSchemaNode';
    id: string;
    archived: boolean;
    format: {
      __typename: 'AntibodyFormatNode';
      id: string;
    };
  }>;
}

interface NonRegistryNodeForValidSchemasSummaryQuery {
  __typename: string;
}

export interface ValidProteinSchemasSummaryQueryResult {
  registries: Array<RegistryNodeForValidSchemasSummaryQuery | NonRegistryNodeForValidSchemasSummaryQuery>;
}

export interface ValidProteinSchemaSummary {
  schema: {
    id: string;
    archived: boolean;
  };
  format: {
    id: string;
  };
}

function isRegistryNode(
  registry: RegistryNodeForValidSchemasSummaryQuery | NonRegistryNodeForValidSchemasSummaryQuery
): registry is RegistryNodeForValidSchemasSummaryQuery {
  return registry.__typename === 'RegistryNode';
}

function toValidProteinSchemaSummary(
  validSchema: RegistryNodeForValidSchemasSummaryQuery['validAntibodySchemas'][number]
): ValidProteinSchemaSummary {
  return {
    schema: {
      id: validSchema.id,
      archived: validSchema.archived,
    },
    format: {
      id: validSchema.format.id,
    },
  };
}

function getValidProteinSchemaSummaries(
  registries: Array<RegistryNodeForValidSchemasSummaryQuery | NonRegistryNodeForValidSchemasSummaryQuery>
): Array<ValidProteinSchemaSummary> {
  const validProteinSchemas: Array<ValidProteinSchemaSummary> = [];
  for (const registry of registries) {
    if (!isRegistryNode(registry)) {
      continue;
    }
    for (const validSchema of registry.validAntibodySchemas) {
      validProteinSchemas.push(toValidProteinSchemaSummary(validSchema));
    }
  }
  return validProteinSchemas;
}

export const VALID_PROTEIN_SCHEMAS_SUMMARY_QUERY = gql`
  query ValidProteinSchemasSummaryQuery @skipAudit @engComponent(name: ANTIBODIES) {
    registries {
      ... on RegistryNode {
        id
        validAntibodySchemas {
          id
          archived
          format {
            id
          }
        }
      }
    }
  }
`;

export function useValidProteinSchemasSummaryQuery(): {
  status: QueryResultType;
  validProteinSchemas: Array<ValidProteinSchemaSummary>;
  refetch: () => Promise<Array<ValidProteinSchemaSummary>>;
} {
  const result = useSkippableBenchlingQuery<ValidProteinSchemasSummaryQueryResult>(
    VALID_PROTEIN_SCHEMAS_SUMMARY_QUERY,
    {
      skip: !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES,
    }
  );

  const queryRefetch = result.refetch;

  const refetch = useCallback(async (): Promise<Array<ValidProteinSchemaSummary>> => {
    const refetchResult = await queryRefetch();
    const registries = refetchResult.data?.registries ?? [];
    return getValidProteinSchemaSummaries(registries);
  }, [queryRefetch]);

  if (result.type !== QueryResultType.SUCCESS) {
    return {status: result.type, validProteinSchemas: [], refetch};
  }

  return {
    status: QueryResultType.SUCCESS,
    validProteinSchemas: getValidProteinSchemaSummaries(result.data.registries),
    refetch,
  };
}
